
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Star } from "lucide-react";

type Testimonial = {
  id: string;
  name: string;
  photo: string;
  university: string;
  text: string;
  service: string;
  rating: number;
};

const testimonials: Testimonial[] = [
  {
    id: "1",
    name: "Jordan Lee",
    photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
    university: "Columbia University",
    text: "The resume review I received from Alumni Insights helped me land interviews at my dream companies. My mentor provided specific feedback that made my experience stand out.",
    service: "Resume Review",
    rating: 5
  },
  {
    id: "2",
    name: "Emily Chen",
    photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
    university: "Stanford University",
    text: "My essay got significantly stronger after working with an alumnus from my target school. They helped me emphasize unique aspects of my background that I hadn't considered.",
    service: "Essay Review",
    rating: 5
  },
  {
    id: "3",
    name: "Marcus Johnson",
    photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
    university: "Harvard University",
    text: "The case preparation sessions were invaluable. My mentor had experience at top consulting firms and provided real-world insights that no case book could offer.",
    service: "Case Preparation",
    rating: 4
  },
  {
    id: "4",
    name: "Sofia Rodriguez",
    photo: "https://images.unsplash.com/photo-1524250502761-1ac6f2e30d43?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
    university: "Yale University",
    text: "The Q&A sessions helped me understand what life is really like at my dream school. This insider perspective was crucial in helping me make my final decision.",
    service: "Q&A Sessions",
    rating: 5
  },
  {
    id: "5",
    name: "Alex Kim",
    photo: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
    university: "MIT",
    text: "The test prep guidance I received was game-changing. My mentor taught me strategies that weren't in any of the prep books, and my GMAT score improved by 70 points.",
    service: "Test Preparation",
    rating: 5
  }
];

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <section className="py-16 bg-gradient-to-br from-crimson/5 to-silver/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">What Our Students Say</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Hear from students who have connected with alumni through our platform
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="relative h-[400px] sm:h-[300px]">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`absolute top-0 left-0 w-full transition-all duration-500 ease-in-out ${
                  index === activeIndex
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-20 pointer-events-none"
                }`}
              >
                <Card className="p-6 md:p-8 bg-white shadow-md">
                  <div className="flex flex-col md:flex-row">
                    <div className="mb-4 md:mb-0 md:mr-6 flex-shrink-0">
                      <img
                        src={testimonial.photo}
                        alt={testimonial.name}
                        className="w-20 h-20 rounded-full object-cover border-2 border-crimson mx-auto md:mx-0"
                      />
                    </div>
                    <div>
                      <div className="flex items-center mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            className={i < testimonial.rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}
                          />
                        ))}
                      </div>
                      <p className="text-gray-600 italic mb-4">"{testimonial.text}"</p>
                      <div>
                        <p className="font-semibold">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.university}</p>
                        <p className="text-sm text-crimson">{testimonial.service}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
          
          <div className="flex justify-center mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-3 h-3 rounded-full mx-1 transition-colors duration-200 ${
                  index === activeIndex ? "bg-crimson" : "bg-silver"
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
